import * as React from "react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { QuantityStepper } from "@/components/ui/quantity-stepper"
import { ConsultationBand, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"
import {
  WinePrice,
  setSelectionCount,
  useSelectedCount,
  wineMetaLine,
  type WineDetailData,
} from "@/components/public/WineCard"
import "@/styles/public-wines.css"

type ShowProps = {
  wine: WineDetailData
  mcc_explainer: string
  back_href: string
}

// The studio pre-selects the bottle shape: sparkling (MCC) wines use the MCC
// bottle, every other style the still-wine bottle.
function studioHref(wineType: string) {
  const bottle = wineType === "mcc" || wineType === "mcc_rose" ? "mcc" : "wine"
  return `/studio?bottle=${bottle}`
}

function paragraphs(text: string) {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
}

// Meta description: the wine's own description, trimmed to ~155 characters
// on a word boundary; otherwise a factual line built from its attributes.
function metaDescription(wine: WineDetailData) {
  if (wine.description) {
    const flat = wine.description.replace(/\s+/g, " ").trim()
    if (flat.length <= 158) return flat
    return `${flat.slice(0, 155).replace(/\s+\S*$/, "")}…`
  }
  return `${wine.name} from ${wine.producer} — a South African ${wine.wine_type_label.toLowerCase()} wine available with a bespoke label from SOH GD Wines, delivered throughout the UK.`
}

export default function WineShow({ wine, mcc_explainer: mccExplainer, back_href: backHref }: ShowProps) {
  const selected = useSelectedCount(wine.id)
  const [draft, setDraft] = React.useState(selected > 0 ? selected : 1)
  React.useEffect(() => {
    if (selected > 0) setDraft(selected)
  }, [selected])

  const description = metaDescription(wine)
  const meta = wineMetaLine(wine)
  const unchanged = selected > 0 && draft === selected
  const actionLabel = selected === 0 ? "Add to your selection" : draft === 0 ? "Remove from your selection" : "Update your selection"

  return (
    <PublicShell>
      <PublicMeta title={wine.name} description={description} />

      <section className="band-black wine-detail">
        <div className="shell">
          <Link href={backHref} className="wine-back">
            <span aria-hidden="true">&larr;</span>{" "}
            {backHref.startsWith("/finder") ? "Back to your finder results" : "Back to the range"}
          </Link>

          <div className="wine-detail-grid">
            <div className="wine-detail-media">
              {wine.detail_image_url ? (
                <img src={wine.detail_image_url} alt={`Bottle of ${wine.name}`} />
              ) : (
                <span className="wine-card-noimage">Image to follow</span>
              )}
            </div>

            <div className="wine-detail-copy">
              <div className="eyebrow">{wine.wine_type_label}</div>
              {wine.mcc && <div className="wine-detail-explainer">MCC — {mccExplainer}</div>}
              <h1>{wine.name}</h1>

              <div className="wine-detail-lines">
                <div className="wine-detail-line">
                  <span className="wine-detail-line-label">Producer</span> {wine.producer}
                </div>
                <div className="wine-detail-line">
                  <span className="wine-detail-line-label">Style</span> {meta}
                </div>
                <div className="wine-detail-line">
                  <span className="wine-detail-line-label">Price</span> <WinePrice pence={wine.unit_price_pence} />
                </div>
              </div>

              {wine.description && (
                <div className="wine-detail-description">
                  {paragraphs(wine.description).map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                </div>
              )}

              {wine.tasting_notes && (
                <section className="wine-detail-section" aria-labelledby="tasting-notes">
                  <h2 id="tasting-notes">Tasting notes</h2>
                  {paragraphs(wine.tasting_notes).map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                </section>
              )}

              {wine.food_pairing && (
                <section className="wine-detail-section" aria-labelledby="food-pairing">
                  <h2 id="food-pairing">Food pairing</h2>
                  {paragraphs(wine.food_pairing).map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                </section>
              )}

              <div className="wine-detail-add">
                <label htmlFor="wine-bottles" className="field-label">
                  Bottles
                </label>
                <div className="wine-detail-add-row">
                  <QuantityStepper
                    id="wine-bottles"
                    label={`Bottles of ${wine.name}`}
                    value={draft}
                    onChange={setDraft}
                  />
                  <Button
                    type="button"
                    variant="gold"
                    size="sm"
                    disabled={unchanged || (selected === 0 && draft === 0)}
                    onClick={() => setSelectionCount(wine.id, draft)}
                  >
                    {actionLabel}
                  </Button>
                </div>
                <div className="wine-detail-add-status" aria-live="polite">
                  {selected > 0
                    ? `${selected} ${selected === 1 ? "bottle" : "bottles"} in your selection.`
                    : "Priced per single bottle. Mix wines freely towards the 100-bottle minimum."}
                </div>
              </div>

              <div className="wine-detail-secondary">
                <Button asChild variant="ghost-hairline" size="sm">
                  <Link href={studioHref(wine.wine_type)}>Design a label for this wine</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ConsultationBand />
    </PublicShell>
  )
}
